/**
 * 系统配置Store（用户端）
 * @description 前端用户页面获取和使用系统运营配置
 * @since 2025-12-27
 */

import type {
  CommissionConfig,
  PointsConfig,
  SystemToggles,
  VipPriceConfig,
} from '@/types/admin/configTypes'
import axios from 'axios'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import * as configApi from '@/api/common/systemConfigApi'

// ═══════════════════════════════════════════════════
// 默认配置（降级使用）
// ═══════════════════════════════════════════════════

const DEFAULT_TOGGLES: SystemToggles = {
  maintenance_mode: false,
  allow_register: true,
  allow_withdraw: true,
  allow_upload: true,
  allow_publish: true,
}

const DEFAULT_VIP_PRICE: VipPriceConfig = {
  individual: { yearly: 99, lifetime: 299 },
  company: { yearly: 299, lifetime: 699 },
}

const DEFAULT_UPLOAD_LIMITS = {
  maxSizeMb: 500,
  maxImages: 10,
}

// ═══════════════════════════════════════════════════
// 用户配置响应类型
// ═══════════════════════════════════════════════════

interface UserConfigData {
  commission: CommissionConfig
  points: PointsConfig
  taskRules: {
    bid: { dailyLimit: number }
    freeCommission: { remaining: number }
  }
  signin: { points: number }
  bidRemaining: number
  userVipLevel: 'normal' | 'vip'
}

// ═══════════════════════════════════════════════════
// 请求去重 Promise 引用
// ═══════════════════════════════════════════════════

let fetchPublicConfigPromise: Promise<void> | null = null

// ═══════════════════════════════════════════════════
// Store定义
// ═══════════════════════════════════════════════════

export const useSystemConfigStore = defineStore('system-config', () => {
  // ─────────────────────────────────────────────────
  // 公开配置（无需登录）
  // ─────────────────────────────────────────────────

  /** 系统开关 */
  const toggles = ref<SystemToggles>(DEFAULT_TOGGLES)

  /** VIP价格配置 */
  const vipPrice = ref<VipPriceConfig>(DEFAULT_VIP_PRICE)

  /** 上传限制配置 */
  const uploadLimits = ref<{ maxSizeMb: number, maxImages: number }>(DEFAULT_UPLOAD_LIMITS)

  /** 配置版本号 */
  const configVersion = ref<number>(0)

  // ─────────────────────────────────────────────────
  // 用户配置（需要登录）
  // ─────────────────────────────────────────────────

  /** 佣金配置 */
  const commission = ref<CommissionConfig | null>(null)

  /** 积分配置 */
  const points = ref<PointsConfig | null>(null)

  /** 今日剩余投标次数 */
  const bidRemaining = ref<number>(0)

  /** 剩余免佣配额 */
  const freeCommissionRemaining = ref<number>(0)

  /** 签到积分 */
  const signinPoints = ref<number>(0)

  /** 用户VIP等级 */
  const userVipLevel = ref<'normal' | 'vip'>('normal')

  // ─────────────────────────────────────────────────
  // 加载状态
  // ─────────────────────────────────────────────────

  /** 公开配置是否已加载 */
  const publicLoaded = ref(false)

  /** 用户配置是否已加载 */
  const userLoaded = ref(false)

  /** 加载中状态 */
  const loading = ref(false)

  /** 加载错误 */
  const error = ref<string | null>(null)

  // ─────────────────────────────────────────────────
  // 计算属性（便捷访问）
  // ─────────────────────────────────────────────────

  /** 是否处于维护模式 */
  const isMaintenanceMode = computed(() => toggles.value.maintenance_mode)

  /** 是否允许注册 */
  const canRegister = computed(() => toggles.value.allow_register)

  /** 是否允许提现 */
  const canWithdraw = computed(() => toggles.value.allow_withdraw)

  /** 是否允许上传 */
  const canUpload = computed(() => toggles.value.allow_upload)

  /** 是否允许发布任务 */
  const canPublish = computed(() => toggles.value.allow_publish)

  /** 是否可以投标（还有剩余次数） */
  const canBid = computed(() => bidRemaining.value > 0)

  /** 是否是VIP用户 */
  const isVip = computed(() => userVipLevel.value === 'vip')

  // ─────────────────────────────────────────────────
  // VIP价格便捷访问
  // ─────────────────────────────────────────────────

  const individualYearlyPrice = computed(() => vipPrice.value.individual.yearly)
  const individualLifetimePrice = computed(() => vipPrice.value.individual.lifetime)
  const companyYearlyPrice = computed(() => vipPrice.value.company.yearly)
  const companyLifetimePrice = computed(() => vipPrice.value.company.lifetime)

  // ─────────────────────────────────────────────────
  // 佣金计算
  // ─────────────────────────────────────────────────

  /** 获取当前用户的模型销售佣金率 */
  const modelCommissionRate = computed(() => {
    if (!commission.value)
      return 0.2
    return isVip.value ? commission.value.model.vip : commission.value.model.normal
  })

  /** 获取当前用户的任务佣金率 */
  const taskCommissionRate = computed(() => {
    if (!commission.value)
      return 0.1
    return isVip.value ? commission.value.task.vip : commission.value.task.normal
  })

  /** 提现手续费率 */
  const withdrawFeeRate = computed(() => commission.value?.withdraw.rate ?? 0.01)

  /** 最低提现金额 */
  const withdrawMinimum = computed(() => commission.value?.withdraw.minimum ?? 10)

  // ─────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────

  /**
   * 获取公开配置（App初始化调用）
   * 包含系统开关、VIP价格、上传限制
   * 使用 Promise 去重：并发调用复用同一个 Promise，避免重复请求
   */
  async function fetchPublicConfig(): Promise<void> {
    if (publicLoaded.value)
      return
    if (fetchPublicConfigPromise)
      return fetchPublicConfigPromise

    fetchPublicConfigPromise = (async () => {
      loading.value = true
      error.value = null

      try {
        const data = await configApi.getPublicConfig()
        toggles.value = data.toggles
        vipPrice.value = data.vipPrice
        uploadLimits.value = data.uploadLimits
        configVersion.value = data.version
        publicLoaded.value = true
      }
      catch (e) {
        if (axios.isCancel(e)) {
          return
        }
        console.error('Failed to fetch public config:', e)
        error.value = '获取配置失败'
      }
      finally {
        loading.value = false
        fetchPublicConfigPromise = null
      }
    })()

    return fetchPublicConfigPromise
  }

  /**
   * 获取用户配置（登录后调用）
   * 包含佣金、积分、投标限制等
   */
  async function fetchUserConfig(): Promise<void> {
    if (userLoaded.value)
      return

    try {
      const data = await configApi.getUserConfig() as UserConfigData | undefined
      if (!data) {
        console.warn('用户配置接口返回空数据，使用默认值')
        userLoaded.value = true
        return
      }
      commission.value = data.commission ?? null
      points.value = data.points ?? null
      bidRemaining.value = data.bidRemaining ?? 0
      freeCommissionRemaining.value = data.taskRules?.freeCommission?.remaining ?? 0
      signinPoints.value = data.signin?.points ?? 0
      userVipLevel.value = data.userVipLevel ?? 'normal'
      userLoaded.value = true
    }
    catch (e) {
      if (axios.isCancel(e)) {
        return
      }
      console.warn('获取用户配置失败:', e)
    }
  }

  /**
   * 重置用户配置（登出时调用）
   */
  function resetUserConfig(): void {
    userLoaded.value = false
    commission.value = null
    points.value = null
    bidRemaining.value = 0
    freeCommissionRemaining.value = 0
    signinPoints.value = 0
    userVipLevel.value = 'normal'
  }

  /**
   * 刷新公开配置（强制重新获取）
   */
  async function refreshPublicConfig(): Promise<void> {
    publicLoaded.value = false
    fetchPublicConfigPromise = null
    await fetchPublicConfig()
  }

  /**
   * 刷新所有配置
   */
  async function refreshAll(): Promise<void> {
    publicLoaded.value = false
    fetchPublicConfigPromise = null
    userLoaded.value = false
    await fetchPublicConfig()
  }

  /**
   * 计算提现实际到账金额
   * @param amount 提现金额
   * @returns 实际到账金额
   */
  function calculateWithdrawAmount(amount: number): number {
    const rate = commission.value?.withdraw.rate ?? 0.01
    return amount * (1 - rate)
  }

  /**
   * 消耗一次投标次数
   */
  function consumeBid(): void {
    if (bidRemaining.value > 0) {
      bidRemaining.value -= 1
    }
  }

  // ─────────────────────────────────────────────────
  // 返回
  // ─────────────────────────────────────────────────

  return {
    // 公开配置
    toggles,
    vipPrice,
    uploadLimits,
    configVersion,

    // 用户配置
    commission,
    points,
    bidRemaining,
    freeCommissionRemaining,
    signinPoints,
    userVipLevel,

    // 加载状态
    publicLoaded,
    userLoaded,
    loading,
    error,

    // 计算属性 - 开关
    isMaintenanceMode,
    canRegister,
    canWithdraw,
    canUpload,
    canPublish,
    canBid,
    isVip,

    // 计算属性 - VIP价格
    individualYearlyPrice,
    individualLifetimePrice,
    companyYearlyPrice,
    companyLifetimePrice,

    // 计算属性 - 佣金
    modelCommissionRate,
    taskCommissionRate,
    withdrawFeeRate,
    withdrawMinimum,

    // Actions
    fetchPublicConfig,
    fetchUserConfig,
    resetUserConfig,
    refreshPublicConfig,
    refreshAll,
    calculateWithdrawAmount,
    consumeBid,
  }
})
